import styled from '@emotion/styled';
import { Button } from 'ds/components/button';
import { SmallText, Text } from 'ds/components/typography';
import { typographyLineHeightCfg } from 'ds/components/typography/types';
import { colorToAlpha } from 'helpers/insight';

export const AutocompleteWrapper = styled.div<{ error: boolean }>`
  ${({ theme, error }) => `
    ${error ? `
      .autocomplete-textfield-wrapper .textfield-input {
        background-color: ${colorToAlpha(theme.colors.alert.red, 0.05)};
        border-color: ${colorToAlpha(theme.colors.alert.red, 0.5)};
        color: ${theme.colors.neutrals.grey1};
      }
    ` : ''}
  `}
`;

export const ValidatedFieldWrapper = styled.div<{ error?: boolean; maxWidth?: number; isWide?: boolean; }>`
  ${({ theme, error, maxWidth, isWide }) => `
    max-width: ${maxWidth || '150'}px;
    width: 100%;
    position: relative;
    display: inline-block;

    ${error ? `
      .textfield-input, .textarea-input {
        background-color: ${colorToAlpha(theme.colors.alert.red, 0.05)};
        border-color: ${colorToAlpha(theme.colors.alert.red, 0.5)};
        color: ${theme.colors.neutrals.grey1};
      }
    ` : ''}

    div {
      border-radius: ${theme.borderRadius.small};
    }

    input {
      direction: rtl;
      ${isWide ? 'width: 300px;' : ''}
    }
  `}

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    margin-left: ${theme.spacing(3)};
  `}

  ${({ theme }) => theme.media.showAt({ to: 2 })`
    flex: 1;
    min-width: 0;
    .textfield-icon {
      left: 12px !important;
    }
    input {
      width: 100%;
      padding-left: 24px;
    }
    div[data-auto="textfield-wrapper"] {
      width: auto;
    }
  `}
`;

export const FieldHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const FieldHeader = styled(SmallText)`
  color: ${({ theme }) => theme.colors.neutrals.grey2};
  text-align: right;
  line-height: ${typographyLineHeightCfg.text}px;
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    margin-left: ${theme.spacing(3)};
  `}
`;

export const RadioGroupWrapper = styled.div<{ error: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  label {
    margin-bottom: unset;
    margin-top: ${({ theme }) => theme.spacing(1.25)};
  }

  ${({ theme, error }) => error ? `
    .radio-input-inner {
      background-color: ${colorToAlpha(theme.colors.alert.red, 0.05)};
      border-color: ${colorToAlpha(theme.colors.alert.red, 0.5)};
    }
  ` : ''}

  ${({ theme }) => theme.media.showAt({ to: 2 })`
    flex-direction: column;
    justify-content: unset;
    label + label {
      margin-top: ${theme.spacing(3)};
    }
  `}
`;

export const FieldWithIconWrapper = styled.div<{ isWide?: boolean; isFillBasedIcon?: boolean }>`
  input.textfield-input {
    padding-right: ${({ theme }) => theme.spacing(1.5)};
  }

  .textfield-icon {
    right: unset;
    left: ${({ theme, isWide }) => isWide ? 0 : theme.spacing(1.5)};
  }

  ${({ isFillBasedIcon }) => isFillBasedIcon ? '' : `
    .textfield-icon[data-auto="textfield-icon"] svg path  {
      fill: none;
    }
  `}
`;

export const SelectFieldWrapper = styled.div<{ error?: boolean; isEmpty?: boolean; isWide?: boolean }>`
  display: flex;
  align-items: baseline;
  ${({ theme, error, isEmpty, isWide = true  }) => `
    ${error ? `
      .select-value-button {
        background-color: ${colorToAlpha(theme.colors.alert.red, 0.05)};
        border-color: ${colorToAlpha(theme.colors.alert.red, 0.5)};
        color: ${theme.colors.neutrals.grey1};
      }
    ` : ''}

    ${isEmpty ? `
      .select-value-text {
        color: ${theme.colors.neutrals.grey4};
      }
    ` : ''}

    button > div {
      text-align: right;
      min-width: ${isWide ? 256 : 94}px;
    }
  `}
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    flex: 1;
    button {
      flex: 1;
    }
    button > div {
      flex: 1;
      min-width: unset;
    }
  `}
`;

export const ChangeBulletinTypePopupSubheader = styled(Text)`
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    text-align: center;
  `}
`;

export const BellErrorIconWrapper = styled.div`
  display: flex;
  width: 80px;
  height: 80px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: ${({ theme }) => theme.colors.alert.redBackground};
`;

export const SuccessIconWrapper = styled.div`
  display: flex;
  width: 80px;
  height: 80px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ChangeBulletinTypePopupRoot = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-items: center;
    align-items: center;
    padding: ${theme.spacing(2)} ${theme.spacing(6)} 0 ${theme.spacing(6)};
  `}
`;

export const AfterUploadSuccessPopupRoot = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-items: center;
    align-items: center;
    padding: ${theme.spacing(1)} ${theme.spacing(2)};
  `}
`;

export const RedButton = styled(Button)`
  width: 220px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  border: ${({ theme }) => theme.colors.alert.red};
  background: ${({ theme }) => theme.colors.alert.red};

  &:hover {
    border: #B40116;
    background: #B40116;
  }
`;

export const SuccessButton = styled(Button)`
  width: 220px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export const CancelWrapper = styled(Text)`
  color: ${({ theme }) => theme.colors.neutrals.grey2};
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.neutrals.grey1};
  }
`;

export const BackWrapper = styled(Text)`
  color: ${({ theme }) => theme.colors.brand.main};
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.brand.darker};
  }
`;

export const DraggableIconWrapper = styled.div`
  padding-left: ${({ theme }) => theme.spacing(1)};
`;

export const PreviewFileWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 160px;
  overflow: hidden;
`;

export const PreviewFile = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  min-width: 0;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.small};

  > button {
    position: absolute;
    left: 6px;
    top: 6px;
    background-color: ${({ theme }) => colorToAlpha(theme.colors.neutrals.black, 0.4)};
    border: none;
  }
`;

export const OtherImagePreview = styled.img`
  position: relative;
  display: block;
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

export const ExclusivityRejectedLabelRoot = styled.div`
  width: fit-content;
`;

export const ExclusivityRejectedLabelWrapper = styled(SmallText)`
  display: flex;
  padding: ${({ theme }) => theme.spacing(0.25)} ${({ theme }) => theme.spacing(1)};
  justify-content: center;
  align-items: center;
  font-weight: 700;
  color: #da3939;
  background: #f9f0f0;
  gap: 4px;
  border-radius: 6px;
  line-height: ${typographyLineHeightCfg.text}px;
`;
